import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaSearch, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./style.css";
import BotaoBusca from "../../components/BotaoBusca.js";
import Loader from "../../components/Loader/index.js";

import {
  carregarAcompanhamentosDoUsuario,
  buscarStatusCompletoDaEmpresa,
} from "./services/carregarAcompanhamentos.js";
import {
  normalizarStatusCadastro,
  STATUS_PARA_ETAPA,
  STATUS_LABELS,
  STATUS_CORES,
  obterTotalEtapasPorPrograma,
} from "./mappers/statusMapper.js";
import { obterEtapaRejeicaoPorHint } from "./mappers/rejeicaoMapper.js";
import { cadastroRequerSupex } from "./mappers/supexMapper.js";
import {
  obterResumoStatusItem,
} from "./ui/itemResumo.js";
import {
  CACHE_KEY_ACOMPANHAMENTO_LISTA,
  carregarCacheComContexto,
  obterIdentidadeSessao,
  salvarCacheComContexto,
} from "./utils/session.js";
import { montarDescricaoEmpresa } from "./utils/empresa.js";
import { formatarCnpjCpf } from "./utils/documento.js";
import {
  obterTipoProgramaUsuarioLogado,
  resolverIdentificacaoPrograma,
} from "./utils/tipoPrograma.js";

/* ============================================================
   Constantes locais (passos visuais do stepper)
   - programa 0 (TCE) e 2 (TCA Empregador): 5 etapas, com
     "Aguardando Preenchimento do Aprendiz" entre cadastro inicial e
     validação, e "Contrato Finalizado" como etapa final;
   - programa 1 (TCA) ou CIEE/consultor: 4 etapas (fluxo padrão).
============================================================ */

const etapasBasePorPrograma = {
  // 0 e 2: o estudante preenche DEPOIS do cadastro inicial e ANTES da validação
  0: [
    { id: 1, nome: "Cadastro inicial" },
    { id: 2, nome: "Aguardando Preenchimento do Aprendiz" },
    { id: 3, nome: "Validação do preenchimento" },
    { id: 4, nome: "Aguardando assinatura" },
    { id: 5, nome: "Contrato Finalizado" },
  ],
  2: [
    { id: 1, nome: "Cadastro inicial" },
    { id: 2, nome: "Aguardando Preenchimento do Aprendiz" },
    { id: 3, nome: "Validação do preenchimento" },
    { id: 4, nome: "Aguardando assinatura" },
    { id: 5, nome: "Contrato Finalizado" },
  ],
  // 1 (TCA) e qualquer outro (CIEE/consultor): fluxo padrão sem etapa extra
  1: [
    { id: 1, nome: "Cadastro inicial" },
    { id: 2, nome: "Validação do preenchimento" },
    { id: 3, nome: "Aguardando assinatura" },
    { id: 4, nome: "Contrato Finalizado" },
  ],
};

const obterEtapasExemplo = (tipoPrograma = 1) =>
  etapasBasePorPrograma[Number(tipoPrograma)] || etapasBasePorPrograma[1];

/* ============================================================
   Constantes de configuração (espelho do CadastroEmpresa)
============================================================ */

const POLL_INTERVAL_MS = 15000;
const PAGE_SIZE = 50;
const CACHE_KEY = CACHE_KEY_ACOMPANHAMENTO_LISTA;
const CACHE_TTL_MS = 30000;

const Stepper = ({ etapaAtual = 1, etapas }) => {
  const etapasSeguras =
    Array.isArray(etapas) && etapas.length > 0
      ? etapas
      : etapasBasePorPrograma[1];
  return (
    <div className="stepper-wrapper">
      <div className="stepper-linha-bg" />
      <div
        className="stepper-linha-progresso"
        style={{
          width: `${
            etapasSeguras.length > 1
              ? ((etapaAtual - 1) / (etapasSeguras.length - 1)) * 100
              : 0
          }%`,
        }}
      />
      <div className="stepper-bolinhas">
        {etapasSeguras.map((etapa, index) => {
          const numero = index + 1;
          const status =
            numero < etapaAtual
              ? "concluida"
              : numero === etapaAtual
                ? "andamento"
                : "pendente";

          return (
            <div className="stepper-etapa" key={etapa.id}>
              <div className={`stepper-bolinha stepper-bolinha-${status}`}>
                {status === "concluida" ? "✓" : numero}
              </div>
              <span className={`stepper-label stepper-label-${status}`}>
                {etapa.nome}
              </span>
            </div>
          );
        })}
      </div>
      <p className="stepper-status-texto">
        Etapa {etapaAtual} de {etapasSeguras.length} —{" "}
        <strong>{etapasSeguras[etapaAtual - 1]?.nome}</strong>
      </p>
    </div>
  );
};

const Acompanhamento = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [itens, setItens] = useState([]);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [buscaDigitada, setBuscaDigitada] = useState("");
  const [temMais, setTemMais] = useState(false);
  const [proximaPagina, setProximaPagina] = useState(2);
  const [carregandoMais, setCarregandoMais] = useState(false);

  const tokenRef = useRef(null);
  const pollingRef = useRef(null);

  const busca = buscaDigitada.trim();

  /* --------------------------------------------------------
     Polling periódico (espelha o iniciarPolling do CadastroEmpresa)
  -------------------------------------------------------- */
  const iniciarPolling = useCallback((empresaRef) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (!empresaRef?.id) return;

    pollingRef.current = setInterval(async () => {
      try {
        const contexto = await buscarStatusCompletoDaEmpresa(
          empresaRef,
          tokenRef.current,
        );
        setItemSelecionado((atual) => {
          if (!atual) return atual;
          return {
            ...atual,
            ...contexto.empresa,
            status: contexto.payload?.status ?? atual.status,
            raw: contexto.payload || atual.raw,
          };
        });
      } catch (error) {
        console.error("Erro ao atualizar status:", error);
        setErro(`Erro ao atualizar status: ${error.message}`);
      }
    }, POLL_INTERVAL_MS);
  }, []);

  useEffect(
    () => () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    },
    [],
  );

  /* --------------------------------------------------------
     Cache leve em sessionStorage com contexto de sessão
  -------------------------------------------------------- */
  const carregarCacheLista = useCallback(() => {
    const cache = carregarCacheComContexto(CACHE_KEY, obterIdentidadeSessao());
    if (!cache || !Array.isArray(cache.lista) || cache.lista.length === 0)
      return null;
    if (Date.now() - Number(cache.atualizadoEm || 0) > CACHE_TTL_MS) return null;
    return cache;
  }, []);

  const salvarCacheLista = useCallback((dados) => {
    salvarCacheComContexto(CACHE_KEY, obterIdentidadeSessao(), dados);
  }, []);

  /* --------------------------------------------------------
     Recarregar lista (cache opcional → rede)
     - usarCache=true: tenta ler do sessionStorage primeiro;
     - usarCache=false: ignora cache (força ida à API).
  -------------------------------------------------------- */
  const recarregarLista = useCallback(
    async (usarCache = true) => {
      tokenRef.current = localStorage.getItem("token");
      if (!tokenRef.current) {
        navigate("/login", { replace: true });
        return;
      }

      if (usarCache) {
        const cache = carregarCacheLista();
        if (cache) {
          setItens(cache.lista);
          setTemMais(Boolean(cache.hasMore));
          setProximaPagina(Number(cache.nextPage) || 2);

          const idPreferido = Number(
            localStorage.getItem("empresa_id_acompanhamento") || 0,
          );
          const inicial =
            cache.lista.find((it) => Number(it.id) === idPreferido) ||
            cache.lista[0];
          if (inicial) {
            setItemSelecionado(inicial);
            iniciarPolling(inicial);
          }
          return;
        }
      }

      try {
        setLoading(true);
        const lista = await carregarAcompanhamentosDoUsuario(
          tokenRef.current,
          1,
          PAGE_SIZE,
        );

        const normalizada = Array.isArray(lista) ? lista : [];
        setItens(normalizada);

        if (normalizada[0]) {
          setItemSelecionado(normalizada[0]);
          iniciarPolling(normalizada[0]);
        }

        const hasMoreApi = normalizada.length === PAGE_SIZE;
        setTemMais(hasMoreApi);
        setProximaPagina(hasMoreApi ? 2 : 1);
        salvarCacheLista({
          lista: normalizada,
          hasMore: hasMoreApi,
          nextPage: hasMoreApi ? 2 : 1,
        });
        setErro("");
      } catch (error) {
        console.error(
          "Erro ao carregar empresa no acompanhamento:",
          error,
        );
        setErro(`Erro ao carregar empresa no acompanhamento: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    [
      carregarCacheLista,
      iniciarPolling,
      navigate,
      salvarCacheLista,
    ],
  );

  /* --------------------------------------------------------
     Carregamento inicial (cache → rede)
  -------------------------------------------------------- */
  useEffect(() => {
    recarregarLista(true);
  }, [recarregarLista]);

  /* --------------------------------------------------------
     Busca por termo livre — disparada SOMENTE via botão,
     conforme preferência do usuário (pt-BR).
  -------------------------------------------------------- */
  const executarBusca = async () => {
    if (!busca) return;

    try {
      setLoading(true);
      setErro("");

      // Reusa o pipeline dos services/mappers para garantir:
      //   1. payload bruto é desmontado (data/itens/{...}/raw) com
      //      normalizarListaEmpresas;
      //   2. cada item vira { id, nome, documento, raw } pela fábrica
      //      normalizarEmpresaAcompanhamentoItem;
      //   3. duplicatas por id são removidas por
      //      deduplicarAcompanhamentosPorEmpresa.
      const lista = await carregarAcompanhamentosDoUsuario(
        tokenRef.current,
        1,
        PAGE_SIZE,
        { busca },
      );

      const dados = Array.isArray(lista) ? lista : [];
      setItens(dados);

      if (dados[0]) {
        setItemSelecionado((atual) =>
          dados.some(
            (d) => (d.id ?? d.tcaId) === (atual?.id ?? atual?.tcaId),
          )
            ? atual
            : dados[0],
        );
      }
    } catch (err) {
      console.error("Erro ao buscar acompanhamentos:", err);
      setErro(
        err.response?.data?.message ||
          err.response?.data?.errors?.join(" | ") ||
          "Erro ao buscar acompanhamentos.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------------
     Seleção de item + atualização do status completo
  -------------------------------------------------------- */
  const selecionarItem = useCallback(
    async (item) => {
      if (!item) return;
      setItemSelecionado(item);
      localStorage.setItem("empresa_id_acompanhamento", String(item.id));
      localStorage.setItem(
        "empresa_busca_acompanhamento",
        montarDescricaoEmpresa(item),
      );

      try {
        const contexto = await buscarStatusCompletoDaEmpresa(
          item,
          tokenRef.current,
        );
        setItemSelecionado((atual) => ({
          ...atual,
          ...contexto.empresa,
          raw: contexto.payload || atual?.raw,
          status: contexto.payload?.status ?? atual?.status,
        }));
        iniciarPolling(item);
      } catch (error) {
        console.error("Erro ao atualizar status:", error);
        setErro(`Erro ao atualizar status: ${error.message}`);
      }
    },
    [iniciarPolling],
  );

  /* --------------------------------------------------------
     Carregar mais (paginação)
  -------------------------------------------------------- */
  const carregarMais = async () => {
    if (!tokenRef.current || !temMais || carregandoMais) return;
    try {
      setCarregandoMais(true);
      const novos = await carregarAcompanhamentosDoUsuario(
        tokenRef.current,
        proximaPagina,
        PAGE_SIZE,
      );

      const idsExistentes = new Set(itens.map((i) => String(i.id)));
      const filtrados = (Array.isArray(novos) ? novos : []).filter(
        (i) => !idsExistentes.has(String(i.id)),
      );

      const proximaLista = [...itens, ...filtrados];
      setItens(proximaLista);

      const aindaTemMais = filtrados.length === PAGE_SIZE;
      const proxima = aindaTemMais ? proximaPagina + 1 : proximaPagina;
      setTemMais(aindaTemMais);
      setProximaPagina(proxima);
      salvarCacheLista({
        lista: proximaLista,
        hasMore: aindaTemMais,
        nextPage: proxima,
      });
    } catch (error) {
      console.error("Erro ao carregar mais acompanhamentos:", error);
      setErro(`Erro ao carregar mais acompanhamentos: ${error.message}`);
    } finally {
      setCarregandoMais(false);
    }
  };

  /* --------------------------------------------------------
     Derivados para render
  -------------------------------------------------------- */
  const listaFiltrada = itens;

  // tipoPrograma do usuário logado (lido do JWT). Define se o stepper
  // exibe a etapa extra "Aguardando Preenchimento do Aprendiz" e
  // "Contrato Finalizado" (programas 0 e 2) ou o fluxo padrão
  // (programa 1 / CIEE).
  const tipoProgramaLogado = obterTipoProgramaUsuarioLogado();
  const etapasExemplo = useMemo(
    () => obterEtapasExemplo(tipoProgramaLogado),
    [tipoProgramaLogado],
  );

  const statusItem = useMemo(() => {
    if (!itemSelecionado) return null;
    const raw = itemSelecionado.raw || itemSelecionado;
    const listaExcecoes = [];
    const requerSupex = cadastroRequerSupex(raw, raw, listaExcecoes, itemSelecionado);
    const etapaRejeicaoHint = obterEtapaRejeicaoPorHint(
      raw,
      raw,
      itemSelecionado,
      requerSupex,
      tipoProgramaLogado,
    );

    return (
      normalizarStatusCadastro(
        itemSelecionado,
        raw,
        requerSupex,
        etapaRejeicaoHint,
        tipoProgramaLogado,
      ) || {
        statusLabel:
          STATUS_LABELS[(itemSelecionado.status || "").toUpperCase()] ||
          itemSelecionado.status ||
          "—",
        statusCor:
          STATUS_CORES[(itemSelecionado.status || "").toUpperCase()] ||
          "#94a3b8",
        etapaAtual:
          STATUS_PARA_ETAPA[(itemSelecionado.status || "").toUpperCase()] || 1,
        totalEtapas: obterTotalEtapasPorPrograma(tipoProgramaLogado, requerSupex),
      }
    );
  }, [itemSelecionado, tipoProgramaLogado]);

  const etapaAtual = statusItem?.etapaAtual ?? 1;

  /* --------------------------------------------------------
     Render
  -------------------------------------------------------- */
  return (
    <div className="acompanhamento-container">
      <header className="acompanhamento-header">
        <div className="acompanhamento-header-titulo">
          <h1>Acompanhamento – CIEEMG</h1>
          <p>Acompanhe o status atual e as próximas etapas do processo.</p>
        </div>
        <button
          className="acompanhamento-btn-voltar"
          onClick={() => navigate("/dashboard")}
        >
          <FaArrowLeft /> Voltar
        </button>
      </header>

      <div className="acompanhamento-filtros">
        <div className="acompanhamento-input-wrapper">
          <FaSearch className="acompanhamento-input-icone" size={14} />
          <input
            type="text"
            placeholder="Buscar por CNPJ, Razão social, Nome do Estudante, CPF, Número do convênio, Código da empresa"
            value={buscaDigitada}
            onChange={(e) => setBuscaDigitada(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && executarBusca()}
            className="acompanhamento-input-busca"
          />
        </div>
        <BotaoBusca onClick={executarBusca} />
      </div>

      <section className="acompanhamento-stepper-card">
        <div className="identificacao-bar">
          <div className="identificacao-item">
            <span className="identificacao-label">CNPJ:</span>
            <span className="identificacao-valor">
              {itemSelecionado
                ? formatarCnpjCpf(
                    itemSelecionado.cnpj ||
                      itemSelecionado.cnpj_ou_cpf ||
                      itemSelecionado.cnpj_empresa ||
                      "",
                  )
                : "—"}
            </span>
            {itemSelecionado &&
              (() => {
                const idPrograma = resolverIdentificacaoPrograma(
                  itemSelecionado,
                  itemSelecionado.cnpj ||
                    itemSelecionado.cnpj_ou_cpf ||
                    itemSelecionado.cnpj_empresa ||
                    "",
                );
                return idPrograma ? (
                  <span
                    className={`acompanhamento-tp-badge ${idPrograma.classe}`}
                    title={
                      idPrograma.tipo === "APRENDIZAGEM"
                        ? "Programa de Aprendizagem"
                        : "Programa de Estágio"
                    }
                  >
                    {idPrograma.rotulo}
                  </span>
                ) : null;
              })()}
          </div>
          <div className="identificacao-item">
            <span className="identificacao-label">Aprendiz:</span>
            <span className="identificacao-valor">
              {itemSelecionado
                ? itemSelecionado.nome_completo ||
                  itemSelecionado.nome ||
                  itemSelecionado.nome_estudante ||
                  "—"
                : "Selecione um registro abaixo"}
            </span>
          </div>
          {statusItem && (
            <div className="identificacao-item">
              <span
                className="status-badge"
                style={{
                  backgroundColor: statusItem.statusCor + "22",
                  color: statusItem.statusCor,
                }}
              >
                {statusItem.statusLabel}
              </span>
            </div>
          )}
        </div>

        <Stepper etapaAtual={etapaAtual} etapas={etapasExemplo} />
      </section>

      {erro && (
        <p className="acompanhamento-vazio" style={{ color: "#dc2626" }}>
          {erro}
        </p>
      )}

      {loading && <Loader message="Buscando autorizações..." />}

      {!loading && listaFiltrada.length === 0 && (
        <p className="acompanhamento-vazio">
          {busca
            ? "Nenhum registro encontrado para o filtro informado."
            : "Nenhum registro encontrado."}
        </p>
      )}

      {!loading && listaFiltrada.length > 0 && (
        <>
          <div className="acompanhamento-lista">
            {listaFiltrada.map((item) => {
              const resumo = obterResumoStatusItem(item, tipoProgramaLogado);
              const id = item.id ?? item.tcaId;
              const nome =
                item.nome_completo ||
                item.nome ||
                item.nome_estudante ||
                item.razao_social ||
                `Registro #${id}`;
              const isSelecionado = itemSelecionado?.id === id;

              return (
                <div
                  className="acompanhamento-card"
                  key={id}
                  onClick={() => selecionarItem(item)}
                  style={
                    isSelecionado
                      ? { borderColor: "#134b8b", borderWidth: 2 }
                      : undefined
                  }
                >
                  <div className="acompanhamento-card-info">
                    <h3>{nome}</h3>
                    {item.cnpj || item.cnpj_ou_cpf ? (
                      <p
                        style={{
                          margin: "0.25rem 0 0",
                          fontSize: 12,
                          color: "#64748b",
                        }}
                      >
                        CNPJ/CPF:{" "}
                        {formatarCnpjCpf(item.cnpj || item.cnpj_ou_cpf)}
                      </p>
                    ) : null}
                    {(() => {
                      const idPrograma = resolverIdentificacaoPrograma(
                        item,
                        item.cnpj || item.cnpj_ou_cpf || "",
                      );
                      return idPrograma ? (
                        <span
                          className={`acompanhamento-tp-badge ${idPrograma.classe}`}
                          title={
                            idPrograma.tipo === "APRENDIZAGEM"
                              ? "Programa de Aprendizagem"
                              : "Programa de Estágio"
                          }
                        >
                          {idPrograma.rotulo}
                        </span>
                      ) : null;
                    })()}
                    <p
                      style={{
                        margin: "0.25rem 0 0",
                        fontSize: 12,
                        color: "#64748b",
                      }}
                    >
                      Etapa {resumo.etapaAtual}/{resumo.totalEtapas} · Progresso{" "}
                      {resumo.progresso}%
                    </p>
                  </div>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: resumo.statusCor + "22",
                      color: resumo.statusCor,
                    }}
                  >
                    {resumo.statusLabel}
                  </span>
                </div>
              );
            })}
          </div>
          {temMais && (
            <div className="acompanhamento-carregar-mais-wrapper">
              <button
                id="acompanhamento-carregar-mais"
                className="btn"
                onClick={carregarMais}
                disabled={carregandoMais}
                style={{ margin: "12px" }}
              >
                {carregandoMais ? "Carregando..." : "Carregar mais"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Acompanhamento;
